import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { EvaluagentAPIService } from '@app/domain/evaluagent/api/evaluagent-api.service';
import { EvaluagentEvaluationObject } from '@app/domain/evaluagent/model/evaluagent-evaluation-object.model';
import { EvaluagentScorecardAverage } from '@app/domain/evaluagent/model/evaluagent-scorecard-average.model';
import { OneToOneScheduleDetailsView } from '@app/domain/one_to_one/model/one-to-one-schedule.model';
import { IState } from '@app/models/state/state.model';
import { Globals } from '@app/shared/globals/globals';
import { firstValueFrom } from 'rxjs';

interface PageEvaluagentScorecardAverage extends EvaluagentScorecardAverage {
  evaluations: EvaluagentEvaluationObject[];
  passRate: number;
}

@Component({
  selector: 'app-information-sidebar-evaluagent',
  templateUrl: './information-sidebar-evaluagent.component.html',
  styleUrls: ['./information-sidebar-evaluagent.component.scss']
})
export class InformationSidebarEvaluagentComponent implements OnInit {

  @Input() userIds: number[];
  //92.29%, 75% pass rate

  userIdUsing: number;
 
  state: IState;
  scorecards: PageEvaluagentScorecardAverage[];

  oldestDate: Date;
  controlDateRangePicker: UntypedFormControl;

  get viewingOwnUserId(): boolean {
    if (!this.userIdUsing) { return false; }
    if (!this.globals.user) { return false; }
    if (this.globals.user.id !== this.userIdUsing ) { return false; }
    return true;
  }

  constructor(
    public globals: Globals,
    private evaluagentApiService: EvaluagentAPIService
  ) {
    this.userIds = [];
    this.state = {
      loading: true,
      error: false,
      errorMessage: ''
    };
    this.scorecards = [];
    this.oldestDate = undefined;
    this.controlDateRangePicker = this.initDateRangeForm();
  }

  ngOnInit(): void {
    this.userIdUsing = this.userIds[0];
    if (!this.userIdUsing) { return; }

    this.populateYearPicker().then(() => {
      this.getData(this.controlDateRangePicker.value.start, new Date());
    });
  }

  populateYearPicker(): Promise<Date> {
    return firstValueFrom(this.evaluagentApiService.getOldestEvaluationPublishDate())
      .then(oldestDate => {
        this.state.loading = true;

        this.oldestDate = oldestDate;
        this.controlDateRangePicker = this.initDateRangeForm(oldestDate);

        return oldestDate;
      });
  }

  getData(startDate: Date, endDate: Date): void {
    firstValueFrom(this.evaluagentApiService.getEvaluationsByUserId(this.userIdUsing, startDate, endDate))
      .then(evaluations => {
        evaluations = evaluations.filter(evaluation => evaluation.scorecard !== 'Complaints Scorecard');
        this.scorecards = this.parseEvaluationsToScorecards(evaluations);
      })
      .finally(() => {
        this.state.loading = false;
      });
  }

  parseEvaluationsToScorecards(evaluations: EvaluagentEvaluationObject[]): PageEvaluagentScorecardAverage[] {
    const evaluationsGroupedByScorecard: {
      [scorecard: string]: {
        evaluations: EvaluagentEvaluationObject[],
        totalScore: number,
        passedCount: number
      }
    } = {};

    evaluations.forEach(evaluation => {
      if (!evaluationsGroupedByScorecard[evaluation.scorecard]) {
        evaluationsGroupedByScorecard[evaluation.scorecard] = {
          evaluations: [],
          totalScore: 0,
          passedCount: 0
        };
      }

      evaluationsGroupedByScorecard[evaluation.scorecard].evaluations.push(evaluation);
      evaluationsGroupedByScorecard[evaluation.scorecard].totalScore += +evaluation.qualityScore;
      if (evaluation.outcomeName === 'PASS') {
        evaluationsGroupedByScorecard[evaluation.scorecard].passedCount++;
      }
    });

    const uniqueScorecards = Object.keys(evaluationsGroupedByScorecard);
    const scorecards: PageEvaluagentScorecardAverage[] = uniqueScorecards.map(scorecard => {
      const scorecardObject = evaluationsGroupedByScorecard[scorecard];

      if (!scorecardObject) { return; }

      const averageScore = (scorecardObject.totalScore / scorecardObject.evaluations.length);

      const pageScorecard: PageEvaluagentScorecardAverage = {
        scorecard: scorecard,
        averageQualityScore: averageScore,
        totalEvaluations: scorecardObject.evaluations.length,
        evaluations: this.sortEvaluations(scorecardObject.evaluations),
        passRate: (scorecardObject.passedCount / scorecardObject.evaluations.length) * 100
      };

      return pageScorecard;
    });

    return scorecards;
  }

  sortEvaluations(evaluationsInput: EvaluagentEvaluationObject[]): EvaluagentEvaluationObject[] {
    // Sort by scorecard
    // return evaluationsInput.sort((a, b) => a.scorecard.localeCompare(b.scorecard));
    // Sort by publish date
    return evaluationsInput.sort((a, b) => {
      const dateA = new Date(a.publishedAt);
      const dateB = new Date(b.publishedAt);
      return dateB.getTime() - dateA.getTime();
    });
  }

  initDateRangeForm(oldestDate?: Date): UntypedFormControl {
    // Default date should be the past 180 days
    let defaultStartDate: Date = new Date();
    defaultStartDate.setDate(defaultStartDate.getDate() - 180);

    // If the oldest date is after the default start date, use the oldest date instead for the range start
    if (oldestDate && oldestDate > defaultStartDate) {
      defaultStartDate = oldestDate;
    }

    const formControl = new UntypedFormControl({
      start: defaultStartDate,
      end: new Date(),
      label: 'Default Range'
    }, []);

    formControl.valueChanges
      .subscribe(newValue => {
        this.getData(newValue.start, newValue.end);
      });

    return formControl;
  }
}
